import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PageHeader } from "../components/PageHeader";
import {
  getCommunityStrategies,
  getBookmarkedStrategies,
} from "../services/strategyService";
import type { StrategyListItem } from "../services/strategyService";
import { runBacktest } from "../services/api";
import type { BacktestResult } from "../stores/backtestStore";
import { useAuthStore } from "../stores/authStore";
import { isBlackoutWindow, getBlackoutMessage } from "../utils/blackout";
import {
  alignCurves,
  computeCorrelationMatrix,
  optimize,
  combinedEquity,
} from "../utils/portfolioOptimizer";
import type {
  OptimizationMode,
  EquityCurve,
  CorrelationResult,
  OptimizationResult,
} from "../utils/portfolioOptimizer";
import { runMonteCarlo } from "../utils/monteCarlo";
import type { MonteCarloResult } from "../utils/monteCarlo";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  Bar,
  Area,
} from "recharts";

// ─── Constants ────────────────────────────────────────────────

const MAX_STRATEGIES = 15;
const MIN_STRATEGIES = 2;
const MAX_CONCURRENT = 3;
const COOLDOWN_MS = 30_000;

const OPTIMIZATION_MODES: { value: OptimizationMode; label: string; desc: string }[] = [
  { value: "max_sharpe", label: "Max Sharpe", desc: "Maximise risk-adjusted return" },
  { value: "max_cagr", label: "Max CAGR", desc: "Maximise compound annual growth rate" },
  { value: "min_drawdown", label: "Min Drawdown", desc: "Minimise worst-case drawdown" },
];

const YEAR_OPTIONS = [1, 2, 3, 5, 7, 10];
const DEFAULT_YEARS = 2;

const MAX_WEIGHT_OPTIONS = [10, 15, 20, 25, 30, 40, 50, 100];
const DEFAULT_MAX_WEIGHT = 20;

const PIE_COLORS = [
  "#06b6d4", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444",
  "#ec4899", "#3b82f6", "#14b8a6", "#f97316", "#a855f7",
  "#22d3ee", "#84cc16", "#e879f9", "#fb923c", "#2dd4bf",
];

// ─── Strategy Backtest Status ─────────────────────────────────

interface StrategyStatus {
  id: string;
  name: string;
  status: "queued" | "running" | "completed" | "failed";
  result?: BacktestResult;
  error?: string;
  elapsed?: number;
}

// ─── Helpers ──────────────────────────────────────────────────

function fmt(val: number | undefined, decimals = 1, suffix = "%"): string {
  if (val === undefined || val === null) return "—";
  return `${val >= 0 ? "+" : ""}${val.toFixed(decimals)}${suffix}`;
}

function fmtUSD(val: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function metricColor(val: number | undefined): string {
  if (val === undefined || val === null) return "text-surface-400";
  return val >= 0 ? "text-emerald-400" : "text-red-400";
}

// ─── Correlation Heatmap ─────────────────────────────────────

function CorrelationHeatmap({ correlation }: { correlation: CorrelationResult }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const n = correlation.labels.length;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cellSize = Math.min(60, Math.floor(500 / n));
    const labelOffset = 120;
    const topLabelOffset = 100;
    const w = labelOffset + n * cellSize;
    const h = topLabelOffset + n * cellSize;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.clearRect(0, 0, w, h);

    // Color interpolation: blue (-1) → white (0) → red (+1)
    function corrColor(val: number): string {
      const clamped = Math.max(-1, Math.min(1, val));
      if (clamped >= 0) {
        const t = clamped;
        const r = Math.round(255);
        const g = Math.round(255 * (1 - t * 0.65));
        const b = Math.round(255 * (1 - t * 0.85));
        return `rgb(${r},${g},${b})`;
      } else {
        const t = -clamped;
        const r = Math.round(255 * (1 - t * 0.85));
        const g = Math.round(255 * (1 - t * 0.55));
        const b = Math.round(255);
        return `rgb(${r},${g},${b})`;
      }
    }

    // Draw cells
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const x = labelOffset + j * cellSize;
        const y = topLabelOffset + i * cellSize;
        ctx.fillStyle = corrColor(correlation.matrix[i][j]);
        ctx.fillRect(x, y, cellSize - 1, cellSize - 1);

        // Value text
        ctx.fillStyle = Math.abs(correlation.matrix[i][j]) > 0.6 ? "#ffffff" : "#1a1a2e";
        ctx.font = `${Math.max(10, cellSize / 4.5)}px monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          correlation.matrix[i][j].toFixed(2),
          x + cellSize / 2,
          y + cellSize / 2,
        );
      }
    }

    // Row labels (left)
    ctx.fillStyle = "#a0a0b8";
    ctx.font = `${Math.max(10, cellSize / 4)}px sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let i = 0; i < n; i++) {
      const label = correlation.labels[i].length > 16
        ? correlation.labels[i].slice(0, 15) + "…"
        : correlation.labels[i];
      ctx.fillText(label, labelOffset - 6, topLabelOffset + i * cellSize + cellSize / 2);
    }

    // Column labels (top, rotated)
    ctx.textAlign = "left";
    for (let j = 0; j < n; j++) {
      const label = correlation.labels[j].length > 16
        ? correlation.labels[j].slice(0, 15) + "…"
        : correlation.labels[j];
      ctx.save();
      ctx.translate(labelOffset + j * cellSize + cellSize / 2, topLabelOffset - 6);
      ctx.rotate(-Math.PI / 4);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
  }, [correlation, n]);

  return (
    <div className="overflow-x-auto">
      <canvas ref={canvasRef} className="mx-auto" />
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────

export function PortfolioLabPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Strategy lists
  const [communityStrategies, setCommunityStrategies] = useState<StrategyListItem[]>([]);
  const [watchlistStrategies, setWatchlistStrategies] = useState<StrategyListItem[]>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"community" | "watchlist">("community");
  const [searchQuery, setSearchQuery] = useState("");

  // Config
  const [portfolioInput, setPortfolioInput] = useState("100000");
  const [backtestYears, setBacktestYears] = useState(DEFAULT_YEARS);
  const [optimizationMode, setOptimizationMode] = useState<OptimizationMode>(
    () => (localStorage.getItem("portfolioLab_mode") as OptimizationMode) || "max_sharpe",
  );
  const [maxWeightCap, setMaxWeightCap] = useState(
    () => Number(localStorage.getItem("portfolioLab_maxWeight")) || DEFAULT_MAX_WEIGHT,
  );
  const [benchmarkTicker, setBenchmarkTicker] = useState(
    () => localStorage.getItem("portfolioLab_benchmark") || "SPY",
  );

  // Analysis state
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [statuses, setStatuses] = useState<StrategyStatus[]>([]);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // Results
  const [correlation, setCorrelation] = useState<CorrelationResult | null>(null);
  const [optimization, setOptimization] = useState<OptimizationResult | null>(null);
  const [backtestResults, setBacktestResults] = useState<Map<string, BacktestResult>>(new Map());
  const [monteCarloResult, setMonteCarloResult] = useState<MonteCarloResult | null>(null);
  const [mcTab, setMcTab] = useState<"fan" | "distribution">("fan");
  const [equityChartData, setEquityChartData] = useState<Record<string, number | string>[] | null>(null);
  const [chartTimeRange, setChartTimeRange] = useState<"1M" | "3M" | "YTD" | "1Y" | "3Y" | "5Y" | "ALL">("ALL");

  // Custom allocation adjustments
  const [customDollars, setCustomDollars] = useState<Record<string, number>>({});
  const [adjustedEquityData, setAdjustedEquityData] = useState<Record<string, number | string>[] | null>(null);
  const [adjustedMonteCarloResult, setAdjustedMonteCarloResult] = useState<MonteCarloResult | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const CHART_RANGES: { label: typeof chartTimeRange; days: number }[] = [
    { label: "1M", days: 30 },
    { label: "3M", days: 90 },
    { label: "YTD", days: -1 },
    { label: "1Y", days: 365 },
    { label: "3Y", days: 1095 },
    { label: "5Y", days: 1825 },
    { label: "ALL", days: Infinity },
  ];

  const filteredEquityData = useMemo(() => {
    if (!equityChartData || equityChartData.length === 0) return equityChartData;

    // Merge adjusted equity data if available
    let data = equityChartData;
    if (adjustedEquityData && adjustedEquityData.length === equityChartData.length) {
      data = equityChartData.map((row, i) => {
        const adj = adjustedEquityData[i];
        return adj?.Adjusted !== undefined ? { ...row, Adjusted: adj.Adjusted } : row;
      });
    }

    if (chartTimeRange === "ALL") return data;
    const lastDate = new Date(data[data.length - 1].date as string);
    let cutoff: Date;
    if (chartTimeRange === "YTD") {
      cutoff = new Date(lastDate.getFullYear(), 0, 1);
    } else {
      const days = CHART_RANGES.find((r) => r.label === chartTimeRange)!.days;
      cutoff = new Date(lastDate);
      cutoff.setDate(cutoff.getDate() - days);
    }
    const sliced = data.filter((p) => new Date(p.date as string) >= cutoff);
    if (sliced.length < 2) return data;
    // Re-normalise to % change from start of visible window
    const keys = Object.keys(sliced[0]).filter((k) => k !== "date");
    const bases: Record<string, number> = {};
    for (const k of keys) bases[k] = Number(sliced[0][k]);
    return sliced.map((row) => {
      const out: Record<string, number | string> = { date: row.date };
      for (const k of keys) {
        const base = bases[k];
        const cur = Number(row[k]);
        // re-normalise: % change from first visible point  
        // Original data is already % change from global start, so back-compute absolute then re-base
        out[k] = base === 0 ? 0 : ((1 + cur / 100) / (1 + base / 100) - 1) * 100;
      }
      return out;
    });
  }, [equityChartData, adjustedEquityData, chartTimeRange]);

  // MC distribution histograms
  const cagrDistribution = useMemo(() => {
    if (!monteCarloResult) return [];
    const { stats } = monteCarloResult;
    const bins = 30;
    const min = stats.percentile5CAGR - 5;
    const max = stats.percentile95CAGR + 5;
    const binWidth = (max - min) / bins;
    const histogram: { range: string; mid: number; count: number; isOriginal: boolean }[] = [];
    for (let i = 0; i < bins; i++) {
      const lo = min + i * binWidth;
      const hi = lo + binWidth;
      const mid = (lo + hi) / 2;
      const z = (mid - stats.medianCAGR) / ((stats.percentile95CAGR - stats.percentile5CAGR) / 3.29);
      const density = Math.exp(-0.5 * z * z);
      histogram.push({ range: `${lo.toFixed(0)}%`, mid, count: Math.round(density * 100), isOriginal: stats.originalCAGR >= lo && stats.originalCAGR < hi });
    }
    return histogram;
  }, [monteCarloResult]);

  const ddDistribution = useMemo(() => {
    if (!monteCarloResult) return [];
    const { stats } = monteCarloResult;
    const bins = 20;
    const min = stats.percentile5MaxDrawdown - 5;
    const max = Math.min(stats.percentile95MaxDrawdown + 5, 0);
    const binWidth = (max - min) / bins;
    const histogram: { range: string; mid: number; count: number; isOriginal: boolean }[] = [];
    for (let i = 0; i < bins; i++) {
      const lo = min + i * binWidth;
      const hi = lo + binWidth;
      const mid = (lo + hi) / 2;
      const z = (mid - stats.medianMaxDrawdown) / ((stats.percentile95MaxDrawdown - stats.percentile5MaxDrawdown) / 3.29);
      const density = Math.exp(-0.5 * z * z);
      histogram.push({ range: `${lo.toFixed(0)}%`, mid, count: Math.round(density * 100), isOriginal: stats.originalMaxDrawdown >= lo && stats.originalMaxDrawdown < hi });
    }
    return histogram;
  }, [monteCarloResult]);

  const portfolioValue = useMemo(() => {
    const parsed = Number(portfolioInput.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 100_000;
  }, [portfolioInput]);

  // ── Load strategies ────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const promises: Promise<void>[] = [
          getCommunityStrategies(100, 0).then((d) => {
            if (!cancelled) setCommunityStrategies(d.strategies || []);
          }),
        ];
        if (isAuthenticated) {
          promises.push(
            getBookmarkedStrategies(100, 0).then((d) => {
              if (!cancelled) setWatchlistStrategies(d.strategies || []);
            }),
          );
        }
        await Promise.all(promises);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load strategies");
      } finally {
        if (!cancelled) setIsLoadingStrategies(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ── Cooldown timer ─────────────────────────────────────────

  useEffect(() => {
    if (lastAnalysisTime === 0) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - lastAnalysisTime));
      setCooldownRemaining(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [lastAnalysisTime]);

  // ── Strategy selection ─────────────────────────────────────

  const toggleStrategy = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_STRATEGIES) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const removeStrategy = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Build combined map for lookups
  const allStrategies = useMemo(() => {
    const map = new Map<string, StrategyListItem>();
    communityStrategies.forEach((s) => map.set(s.id, s));
    watchlistStrategies.forEach((s) => map.set(s.id, s));
    return map;
  }, [communityStrategies, watchlistStrategies]);

  const selectedStrategies = useMemo(
    () => [...selectedIds].map((id) => allStrategies.get(id)).filter(Boolean) as StrategyListItem[],
    [selectedIds, allStrategies],
  );

  // Filtered list for current tab
  const filteredStrategies = useMemo(() => {
    const list = activeTab === "community" ? communityStrategies : watchlistStrategies;
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.slug.toLowerCase().includes(q) ||
        (s.description && s.description.toLowerCase().includes(q)),
    );
  }, [activeTab, communityStrategies, watchlistStrategies, searchQuery]);

  // ── Save optimization mode to localStorage ─────────────────

  useEffect(() => {
    localStorage.setItem("portfolioLab_mode", optimizationMode);
  }, [optimizationMode]);

  useEffect(() => {
    localStorage.setItem("portfolioLab_maxWeight", String(maxWeightCap));
  }, [maxWeightCap]);

  useEffect(() => {
    localStorage.setItem("portfolioLab_benchmark", benchmarkTicker);
  }, [benchmarkTicker]);

  // ── Re-optimise when mode or portfolio value changes ───────

  useEffect(() => {
    if (backtestResults.size < MIN_STRATEGIES || !correlation) return;
    runOptimization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizationMode, maxWeightCap]);

  // ── Run Analysis ───────────────────────────────────────────

  const canAnalyse = selectedIds.size >= MIN_STRATEGIES && !isAnalysing && cooldownRemaining === 0;

  const runAnalysis = useCallback(async () => {
    // Blackout check
    if (isBlackoutWindow()) {
      setAnalysisError(getBlackoutMessage());
      return;
    }

    if (!canAnalyse) return;

    setIsAnalysing(true);
    setAnalysisError(null);
    setCorrelation(null);
    setOptimization(null);

    const strategies = selectedStrategies;
    const initialStatuses: StrategyStatus[] = strategies.map((s) => ({
      id: s.id,
      name: s.name,
      status: "queued",
    }));
    setStatuses(initialStatuses);

    const results = new Map<string, BacktestResult>();
    const controller = new AbortController();
    abortRef.current = controller;

    // Semaphore for concurrency limiting
    let running = 0;
    let idx = 0;
    const statusesCopy = [...initialStatuses];

    const updateStatus = (i: number, patch: Partial<StrategyStatus>) => {
      Object.assign(statusesCopy[i], patch);
      setStatuses([...statusesCopy]);
    };

    await new Promise<void>((resolveAll) => {
      function launch() {
        while (running < MAX_CONCURRENT && idx < strategies.length) {
          if (controller.signal.aborted) break;
          const currentIdx = idx++;
          running++;

          const startTime = Date.now();
          updateStatus(currentIdx, { status: "running" });

          runBacktest("", backtestYears * 252, {
            strategyId: strategies[currentIdx].id,
            useFastMode: true,
            initialCapital: 100000,
            benchmarkTicker: benchmarkTicker.trim().toUpperCase() || "SPY",
          })
            .then((result) => {
              results.set(strategies[currentIdx].id, result);
              updateStatus(currentIdx, {
                status: "completed",
                result,
                elapsed: (Date.now() - startTime) / 1000,
              });
            })
            .catch((err) => {
              const msg = err instanceof Error ? err.message : "Backtest failed";
              updateStatus(currentIdx, {
                status: "failed",
                error: msg,
                elapsed: (Date.now() - startTime) / 1000,
              });
            })
            .finally(() => {
              running--;
              if (idx >= strategies.length && running === 0) {
                resolveAll();
              } else {
                launch();
              }
            });
        }
        if (strategies.length === 0) resolveAll();
      }
      launch();
    });

    abortRef.current = null;
    setBacktestResults(results);
    setLastAnalysisTime(Date.now());

    // Check if we have enough results
    if (results.size < MIN_STRATEGIES) {
      setAnalysisError(
        `Only ${results.size} backtest(s) succeeded. Need at least ${MIN_STRATEGIES} for correlation analysis.`,
      );
      setIsAnalysing(false);
      return;
    }

    // Build equity curves from results
    const curves: EquityCurve[] = [];
    for (const [id, res] of results.entries()) {
      const strat = allStrategies.get(id);
      curves.push({
        strategyId: id,
        strategyName: strat?.name || res.strategyName,
        dates: res.equity.map((e) => e.date),
        values: res.equity.map((e) => e.value),
      });
    }

    // Align and compute
    const { returns, labels, ids } = alignCurves(curves);

    if (returns[0].length < 30) {
      setAnalysisError("Insufficient overlapping trading days between strategies. Need at least 30 days.");
      setIsAnalysing(false);
      return;
    }

    const corr = computeCorrelationMatrix(returns, labels, ids);
    setCorrelation(corr);

    // Expected returns from backtest CAGR
    const expectedReturns = ids.map((id) => results.get(id)?.metrics.cagr ?? 0);

    const opt = optimize(optimizationMode, returns, expectedReturns, labels, ids, 0, maxWeightCap / 100);
    setOptimization(opt);

    // Initialize custom dollars from optimizer weights
    setCustomDollars(buildDollarAllocation(opt.ids, opt.weights, portfolioValue));
    setAdjustedEquityData(null);
    setAdjustedMonteCarloResult(null);

    // Extract benchmark equity from first successful result
    const benchmarkData = extractBenchmark(results);

    // Build equity chart data (combined + individual curves normalised to %)
    buildEquityChart(curves, opt.weights, labels, ids, benchmarkData);

    // Run Monte Carlo on combined portfolio
    const combined = combinedEquity(returns, opt.weights);
    const { alignedDates: mcDates } = alignCurves(curves);
    const mcResult = runMonteCarlo(["start", ...mcDates], combined);
    attachBenchmarkToMC(mcResult, benchmarkData);
    setMonteCarloResult(mcResult);

    setIsAnalysing(false);
  }, [canAnalyse, selectedStrategies, allStrategies, optimizationMode, maxWeightCap, backtestYears, benchmarkTicker, portfolioValue]);

  // Extract benchmark equity from first successful backtest result
  const extractBenchmark = (results: Map<string, BacktestResult>): Map<string, number> => {
    const benchMap = new Map<string, number>();
    for (const [, res] of results) {
      if (res.equity?.length > 0) {
        for (const pt of res.equity) {
          if (pt.benchmark != null && !benchMap.has(pt.date)) {
            benchMap.set(pt.date, pt.benchmark);
          }
        }
        if (benchMap.size > 0) break; // only need one result's benchmark
      }
    }
    return benchMap;
  };

  // Attach benchmark equity values to MC band data for charting
  const attachBenchmarkToMC = (mcResult: MonteCarloResult, benchmarkData: Map<string, number>) => {
    if (benchmarkData.size === 0) return;
    for (const band of mcResult.bands) {
      const val = benchmarkData.get(band.date);
      if (val != null) {
        band.benchmark = val;
      }
    }
  };

  // Build equity chart data for combined + individual curves + benchmark
  const buildEquityChart = useCallback(
    (curves: EquityCurve[], weights: number[], labels: string[], _ids: string[], benchmarkData?: Map<string, number>) => {
      const { returns, alignedDates } = alignCurves(curves);
      const combined = combinedEquity(returns, weights);

      // Also build individual equity curves (normalised to same start)
      const individualEquities = returns.map((r) => {
        const eq = [100000];
        for (let d = 0; d < r.length; d++) {
          eq.push(eq[eq.length - 1] * (1 + r[d]));
        }
        return eq;
      });

      // Build benchmark aligned to same dates
      let benchAligned: number[] | null = null;
      if (benchmarkData && benchmarkData.size > 0) {
        const firstDate = alignedDates[0];
        const firstBench = benchmarkData.get(firstDate);
        if (firstBench && firstBench > 0) {
          benchAligned = alignedDates.map((d) => {
            const val = benchmarkData.get(d);
            return val != null ? val : NaN;
          });
        }
      }

      // Build chart data: normalised to % change from start
      const chartData: Record<string, number | string>[] = alignedDates.map((date, i) => {
        const row: Record<string, number | string> = { date };
        row["Combined"] = ((combined[i + 1] / combined[0]) - 1) * 100;
        for (let s = 0; s < labels.length; s++) {
          row[labels[s]] = ((individualEquities[s][i + 1] / individualEquities[s][0]) - 1) * 100;
        }
        if (benchAligned && !isNaN(benchAligned[i]) && !isNaN(benchAligned[0]) && benchAligned[0] > 0) {
          row["Benchmark"] = ((benchAligned[i] / benchAligned[0]) - 1) * 100;
        }
        return row;
      });

      setEquityChartData(chartData);
    },
    [],
  );

  // Re-optimise without re-running backtests
  const runOptimization = useCallback(() => {
    if (backtestResults.size < MIN_STRATEGIES) return;

    const curves: EquityCurve[] = [];
    for (const [id, res] of backtestResults.entries()) {
      const strat = allStrategies.get(id);
      curves.push({
        strategyId: id,
        strategyName: strat?.name || res.strategyName,
        dates: res.equity.map((e) => e.date),
        values: res.equity.map((e) => e.value),
      });
    }

    const { returns, labels, ids } = alignCurves(curves);
    const expectedReturns = ids.map((id) => backtestResults.get(id)?.metrics.cagr ?? 0);
    const opt = optimize(optimizationMode, returns, expectedReturns, labels, ids, 0, maxWeightCap / 100);
    setOptimization(opt);

    // Initialize custom dollars from optimizer weights
    setCustomDollars(buildDollarAllocation(opt.ids, opt.weights, portfolioValue));
    setAdjustedEquityData(null);
    setAdjustedMonteCarloResult(null);

    // Rebuild equity chart + MC for new weights
    const benchmarkData = extractBenchmark(backtestResults);
    buildEquityChart(curves, opt.weights, labels, ids, benchmarkData);
    const combined = combinedEquity(returns, opt.weights);
    const { alignedDates: mcDates } = alignCurves(curves);
    const mcResult = runMonteCarlo(["start", ...mcDates], combined);
    attachBenchmarkToMC(mcResult, benchmarkData);
    setMonteCarloResult(mcResult);
  }, [backtestResults, allStrategies, optimizationMode, maxWeightCap, portfolioValue, buildEquityChart]);

  // ── Custom allocation helpers ──────────────────────────────

  /** Distribute dollar amounts across strategies such that they sum to exactly portfolioValue */
  const buildDollarAllocation = useCallback((ids: string[], weights: number[], total: number): Record<string, number> => {
    const dollars: Record<string, number> = {};
    let sum = 0;
    let maxIdx = 0;
    ids.forEach((id, i) => {
      const amt = Math.round(total * weights[i]);
      dollars[id] = amt;
      sum += amt;
      if (weights[i] > weights[maxIdx]) maxIdx = i;
    });
    // Correct rounding drift on the largest allocation
    if (sum !== total && ids.length > 0) {
      dollars[ids[maxIdx]] += total - sum;
    }
    return dollars;
  }, []);

  const handleCustomDollarChange = useCallback((id: string, value: string) => {
    const parsed = Number(value.replace(/[^0-9.]/g, ""));
    setCustomDollars((prev) => ({
      ...prev,
      [id]: Number.isFinite(parsed) ? Math.round(parsed) : 0,
    }));
  }, []);

  const customTotal = useMemo(() => {
    return Object.values(customDollars).reduce((s, v) => s + v, 0);
  }, [customDollars]);

  const hasCustomChanges = useMemo(() => {
    if (!optimization) return false;
    const baseline = buildDollarAllocation(optimization.ids, optimization.weights, portfolioValue);
    return optimization.ids.some((id) => customDollars[id] !== baseline[id]);
  }, [optimization, customDollars, portfolioValue, buildDollarAllocation]);

  const handleRecalculate = useCallback(() => {
    if (!optimization || !backtestResults.size) return;
    setIsRecalculating(true);

    const total = Object.values(customDollars).reduce((s, v) => s + v, 0);
    if (total <= 0) {
      setIsRecalculating(false);
      return;
    }

    // Normalize custom dollars to weights
    const customWeights = optimization.ids.map((id) => (customDollars[id] || 0) / total);

    const curves: EquityCurve[] = [];
    for (const [id, res] of backtestResults.entries()) {
      const strat = allStrategies.get(id);
      curves.push({
        strategyId: id,
        strategyName: strat?.name || res.strategyName,
        dates: res.equity.map((e) => e.date),
        values: res.equity.map((e) => e.value),
      });
    }

    const { returns, alignedDates } = alignCurves(curves);
    const adjustedCombined = combinedEquity(returns, customWeights);

    // Build adjusted equity data (same date array as equityChartData)
    const adjData: Record<string, number | string>[] = alignedDates.map((date, i) => ({
      date,
      Adjusted: ((adjustedCombined[i + 1] / adjustedCombined[0]) - 1) * 100,
    }));
    setAdjustedEquityData(adjData);

    // Run adjusted Monte Carlo
    const mcResult = runMonteCarlo(["start", ...alignedDates], adjustedCombined);
    const benchmarkData = extractBenchmark(backtestResults);
    attachBenchmarkToMC(mcResult, benchmarkData);
    setAdjustedMonteCarloResult(mcResult);

    setIsRecalculating(false);
  }, [optimization, customDollars, backtestResults, allStrategies]);

  const handleResetCustom = useCallback(() => {
    if (!optimization) return;
    setCustomDollars(buildDollarAllocation(optimization.ids, optimization.weights, portfolioValue));
    setAdjustedEquityData(null);
    setAdjustedMonteCarloResult(null);
  }, [optimization, portfolioValue, buildDollarAllocation]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // ── Progress ───────────────────────────────────────────────

  const completedCount = statuses.filter((s) => s.status === "completed").length;
  const failedCount = statuses.filter((s) => s.status === "failed").length;
  const totalCount = statuses.length;
  const progressPct = totalCount > 0 ? ((completedCount + failedCount) / totalCount) * 100 : 0;

  // ── Render ─────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Portfolio Analyzer"
        subtitle="Correlation analysis & optimised allocation across strategies"
        backgroundImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* ── Blackout Banner ──────────────────────────── */}
        {isBlackoutWindow() && (
          <div className="mb-6 rounded-lg bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-300">{getBlackoutMessage()}</p>
          </div>
        )}

        {/* ── Quote ──────────────────────────────────── */}
        <blockquote className="mb-6 border-l-2 border-primary-500/40 pl-4 py-1">
          <p className="text-sm italic text-surface-400">
            "The only free lunch in investing is diversification."
          </p>
          <cite className="mt-1 block text-xs text-surface-500 not-italic">
            — Harry Markowitz, Nobel Laureate
          </cite>
        </blockquote>

        {/* ── How it works ───────────────────────────── */}
        <div className="mb-6 rounded-lg bg-surface-800/40 border border-surface-700/50 px-4 py-3">
          <p className="text-sm text-surface-400 leading-relaxed">
            <span className="text-surface-200 font-medium">Build a diversified portfolio:</span>{" "}
            Select 2–15 strategies → choose backtest period &amp; max weight cap → click Analyse.
            The engine backtests each strategy, computes correlations, and suggests optimal allocations
            using your chosen mode (Sharpe, CAGR, or Min Drawdown). Monte Carlo simulates 5,000
            portfolio paths to stress-test the result.
          </p>
        </div>

        {/* ── Strategy Picker ────────────────────────── */}
        <div className="glass-card p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-surface-100">Select Strategies</h2>
            <span className="text-sm text-surface-400">
              <span className={selectedIds.size >= MIN_STRATEGIES ? "text-primary-400" : "text-surface-400"}>
                {selectedIds.size}
              </span>
              /{MAX_STRATEGIES} selected
            </span>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setActiveTab("community")}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                activeTab === "community"
                  ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                  : "bg-surface-800/50 text-surface-400 border border-surface-700/50 hover:text-surface-200"
              }`}
            >
              Community
            </button>
            <button
              onClick={() => setActiveTab("watchlist")}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                activeTab === "watchlist"
                  ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                  : "bg-surface-800/50 text-surface-400 border border-surface-700/50 hover:text-surface-200"
              }`}
            >
              My Watchlist
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search strategies..."
              className="input-field w-full pl-10 py-2 text-sm"
            />
          </div>

          {/* Selected chips */}
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedStrategies.map((s) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 text-primary-400 text-xs border border-primary-500/30"
                >
                  {s.name}
                  <button
                    onClick={() => removeStrategy(s.id)}
                    className="hover:text-primary-200 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Strategy list */}
          {isLoadingStrategies ? (
            <div className="flex items-center justify-center py-10">
              <div className="spinner w-6 h-6" />
              <span className="ml-3 text-surface-400 text-sm">Loading strategies...</span>
            </div>
          ) : loadError ? (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {loadError}
            </div>
          ) : activeTab === "watchlist" && !isAuthenticated ? (
            <p className="text-sm text-surface-500 py-6 text-center">
              Log in to see your watchlist strategies.
            </p>
          ) : filteredStrategies.length === 0 ? (
            <p className="text-sm text-surface-500 py-6 text-center">
              {searchQuery ? "No strategies match your search." : "No strategies available."}
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-1 sidebar-scrollbar">
              {filteredStrategies.map((s) => {
                const checked = selectedIds.has(s.id);
                const disabled = !checked && selectedIds.size >= MAX_STRATEGIES;
                return (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                      checked
                        ? "bg-primary-500/10 border border-primary-500/20"
                        : disabled
                          ? "opacity-40 cursor-not-allowed border border-transparent"
                          : "hover:bg-surface-800/50 border border-transparent"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleStrategy(s.id)}
                      className="rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500/50"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-surface-200 truncate">{s.name}</div>
                      {s.description && (
                        <div className="text-xs text-surface-500 truncate">{s.description}</div>
                      )}
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-xs font-mono shrink-0">
                      <span className={metricColor(s.cagr)}>{fmt(s.cagr)}</span>
                      <span className="text-red-400">
                        {s.max_drawdown !== undefined ? `-${Math.abs(s.max_drawdown).toFixed(1)}%` : "—"}
                      </span>
                      <span className="text-surface-300">{s.sharpe?.toFixed(2) ?? "—"}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Controls ───────────────────────────────── */}
        <div className="glass-card p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-6">
            {/* Portfolio value */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">Portfolio Value</label>
              <div className="relative w-full sm:w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500 text-sm">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={portfolioInput}
                  onChange={(e) => setPortfolioInput(e.target.value)}
                  className="input-field w-full pl-7 py-2 text-right"
                  placeholder="100000"
                />
              </div>
            </div>

            {/* Benchmark ticker */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">Benchmark</label>
              <input
                type="text"
                value={benchmarkTicker}
                onChange={(e) => setBenchmarkTicker(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
                className="input-field w-24 py-2 text-center uppercase tracking-wider"
                placeholder="SPY"
                maxLength={5}
              />
            </div>

            {/* Backtest period */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">Backtest Period</label>
              <div className="flex gap-1.5 flex-wrap">
                {YEAR_OPTIONS.map((y) => (
                  <button
                    key={y}
                    onClick={() => setBacktestYears(y)}
                    className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                      backtestYears === y
                        ? "bg-primary-500/20 text-primary-400 border-primary-500/30"
                        : "bg-surface-800/50 text-surface-400 border-surface-700/50 hover:text-surface-200"
                    }`}
                  >
                    {y}Y
                  </button>
                ))}
              </div>
            </div>

            {/* Max Weight Cap */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">Max Weight / Strategy</label>
              <div className="flex gap-1.5 flex-wrap">
                {MAX_WEIGHT_OPTIONS.map((pct) => (
                  <button
                    key={pct}
                    onClick={() => setMaxWeightCap(pct)}
                    className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                      maxWeightCap === pct
                        ? "bg-primary-500/20 text-primary-400 border-primary-500/30"
                        : "bg-surface-800/50 text-surface-400 border-surface-700/50 hover:text-surface-200"
                    }`}
                    title={pct === 100 ? "No cap" : `No strategy can exceed ${pct}% allocation`}
                  >
                    {pct === 100 ? "None" : `${pct}%`}
                  </button>
                ))}
              </div>
              {maxWeightCap < 100 && selectedIds.size > 0 && selectedIds.size * (maxWeightCap / 100) < 1 && (
                <p className="mt-1.5 text-xs text-amber-400">
                  {selectedIds.size} strategies × {maxWeightCap}% cap = {selectedIds.size * maxWeightCap}% max.
                  Cap will be raised to {(100 / selectedIds.size).toFixed(0)}% (equal weight) to reach 100%.
                </p>
              )}
            </div>

            {/* Optimization mode */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-surface-300 mb-2">Optimisation Mode</label>
              <div className="flex gap-2 flex-wrap">
                {OPTIMIZATION_MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setOptimizationMode(m.value)}
                    className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                      optimizationMode === m.value
                        ? "bg-primary-500/20 text-primary-400 border-primary-500/30"
                        : "bg-surface-800/50 text-surface-400 border-surface-700/50 hover:text-surface-200"
                    }`}
                    title={m.desc}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Analyze button */}
            <div className="shrink-0">
              <button
                onClick={runAnalysis}
                disabled={!canAnalyse || isBlackoutWindow()}
                className="btn btn-primary px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isAnalysing ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-4 h-4" />
                    Analysing...
                  </span>
                ) : cooldownRemaining > 0 ? (
                  `Wait ${Math.ceil(cooldownRemaining / 1000)}s`
                ) : (
                  "Analyse Portfolio"
                )}
              </button>
              {selectedIds.size < MIN_STRATEGIES && (
                <p className="text-xs text-surface-500 mt-1">Select at least {MIN_STRATEGIES} strategies</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Analysis Error ─────────────────────────── */}
        {analysisError && (
          <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 p-4 text-sm text-red-400 flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.999L13.732 4.001c-.77-1.333-2.694-1.333-3.464 0L3.268 16.001C2.498 17.334 3.46 19 5 19z" />
            </svg>
            <p>{analysisError}</p>
          </div>
        )}

        {/* ── Progress Panel ─────────────────────────── */}
        {isAnalysing && statuses.length > 0 && (
          <div className="glass-card p-6 mb-6 animate-slide-up">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-surface-100">Building Portfolio Analysis</h3>
              <span className="text-sm text-surface-400">
                {completedCount + failedCount} of {totalCount} strategies
              </span>
            </div>

            {/* Progress bar */}
            <div className="progress-bar mb-4">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Per-strategy statuses */}
            <div className="space-y-2">
              {statuses.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-surface-900/50"
                >
                  <div className="flex items-center gap-3">
                    {s.status === "completed" && (
                      <span className="text-accent-400 text-sm">✅</span>
                    )}
                    {s.status === "running" && (
                      <div className="spinner w-4 h-4" />
                    )}
                    {s.status === "queued" && (
                      <span className="w-4 h-4 rounded-full border border-surface-600 inline-block" />
                    )}
                    {s.status === "failed" && (
                      <span className="text-red-400 text-sm">❌</span>
                    )}
                    <span className={`text-sm ${
                      s.status === "completed" ? "text-surface-200" :
                      s.status === "running" ? "text-primary-400 font-medium" :
                      s.status === "failed" ? "text-red-400" :
                      "text-surface-500"
                    }`}>
                      {s.name}
                    </span>
                  </div>
                  <span className="text-xs text-surface-500">
                    {s.status === "running" && "running..."}
                    {s.status === "completed" && s.elapsed !== undefined && `${s.elapsed.toFixed(1)}s`}
                    {s.status === "failed" && (s.error || "failed")}
                    {s.status === "queued" && "queued"}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs text-surface-500 text-center">
              First run may take 1–2 minutes. Subsequent runs are faster due to price caching.
            </p>
          </div>
        )}

        {/* ── Failed strategies warning ──────────────── */}
        {!isAnalysing && failedCount > 0 && correlation && (
          <div className="mb-6 rounded-lg bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-300 flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.999L13.732 4.001c-.77-1.333-2.694-1.333-3.464 0L3.268 16.001C2.498 17.334 3.46 19 5 19z" />
            </svg>
            <p>
              {failedCount} strategy(ies) could not be analysed:{" "}
              {statuses.filter((s) => s.status === "failed").map((s) => s.name).join(", ")}.
              Results are based on the remaining {completedCount} strategies.
            </p>
          </div>
        )}

        {/* ── Results ────────────────────────────────── */}
        {!isAnalysing && correlation && optimization && (
          <div className="animate-fade-in space-y-6">
            {/* Combined Portfolio Stats */}
            <div>
              <h2 className="text-lg font-semibold text-surface-100 mb-4">Portfolio Statistics</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard
                  label="Expected CAGR"
                  value={fmt(optimization.expectedReturn)}
                  color={metricColor(optimization.expectedReturn)}
                />
                <StatCard
                  label="Expected Max DD"
                  value={fmt(optimization.expectedMaxDD)}
                  color="text-red-400"
                />
                <StatCard
                  label="Portfolio Sharpe"
                  value={optimization.portfolioSharpe.toFixed(2)}
                  color="text-primary-400"
                />
                <StatCard
                  label="Volatility"
                  value={fmt(optimization.expectedVolatility)}
                  color="text-amber-400"
                />
                <StatCard
                  label="Diversification"
                  value={`${optimization.diversificationBenefit.toFixed(1)}%`}
                  color="text-emerald-400"
                />
                <StatCard
                  label="Strategies"
                  value={String(optimization.labels.length)}
                  color="text-surface-200"
                />
              </div>
            </div>

            {/* Two-column: Heatmap + Allocation */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Correlation Heatmap */}
              <div className="glass-card p-5">
                <h3 className="text-base font-semibold text-surface-100 mb-4">Correlation Matrix</h3>
                <CorrelationHeatmap correlation={correlation} />
                <p className="mt-3 text-xs text-surface-500 text-center">
                  Blue = negative correlation (good diversification) · Red = positive correlation
                </p>
              </div>

              {/* Allocation Pie Chart + Table */}
              <div className="glass-card p-5">
                <h3 className="text-base font-semibold text-surface-100 mb-4">
                  Suggested Allocation
                  <span className="text-xs font-normal text-surface-500 ml-2">
                    {fmtUSD(portfolioValue)} portfolio
                  </span>
                </h3>

                {/* Pie Chart */}
                <div className="h-52 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={optimization.weights
                          .map((w, i) => ({ name: optimization.labels[i], value: Math.round(w * 1000) / 10 }))
                          .filter((d) => d.value > 0.5)
                          .sort((a, b) => b.value - a.value)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        strokeWidth={1}
                        stroke="rgba(15,15,30,0.6)"
                        label={({ name, value }: { name?: string; value?: number }) => `${(name ?? "").length > 12 ? (name ?? "").slice(0, 11) + "…" : (name ?? "")} ${value}%`}
                        labelLine={{ stroke: "#64748b", strokeWidth: 1 }}
                        isAnimationActive={false}
                      >
                        {optimization.weights.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={0.8} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0];
                          return (
                            <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-sm">
                              <span className="text-surface-200">{d.name}: </span>
                              <span className="text-primary-400 font-medium">{String(d.value)}%</span>
                              <span className="text-surface-400 ml-2">
                                ({fmtUSD(portfolioValue * (Number(d.value) / 100))})
                              </span>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Allocation table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-700/50">
                        <th className="text-left px-3 py-2 text-surface-400 font-semibold">Strategy</th>
                        <th className="text-right px-3 py-2 text-surface-400 font-semibold">Weight</th>
                        <th className="text-left px-3 py-2 text-surface-400 font-semibold w-32">Alloc</th>
                        <th className="text-right px-3 py-2 text-surface-400 font-semibold">Amount</th>
                        <th className="text-right px-3 py-2 text-surface-400 font-semibold">
                          <span className="text-amber-400">Your $</span>
                        </th>
                        <th className="text-right px-3 py-2 text-surface-400 font-semibold">CAGR</th>
                        <th className="text-right px-3 py-2 text-surface-400 font-semibold">Max DD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optimization.weights
                        .map((w, i) => ({ w, label: optimization.labels[i], id: optimization.ids[i] }))
                        .sort((a, b) => b.w - a.w)
                        .filter(({ w }) => w > 0.001) // hide truly zero allocations
                        .map(({ w, label, id }) => {
                          const pct = w * 100;
                          const amount = portfolioValue * w;
                          const res = backtestResults.get(id);
                          return (
                            <tr key={id} className="border-b border-surface-800/50 hover:bg-surface-800/30 transition-colors">
                              <td className="px-3 py-2.5">
                                <div className="font-medium text-surface-200 truncate max-w-[200px]">{label}</div>
                              </td>
                              <td className="text-right px-3 py-2.5 font-mono text-primary-400">
                                {pct.toFixed(1)}%
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="w-full bg-surface-800 rounded-full h-2">
                                  <div
                                    className="bg-primary-500/60 h-2 rounded-full transition-all"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </td>
                              <td className="text-right px-3 py-2.5 font-mono text-surface-200">
                                {fmtUSD(amount)}
                              </td>
                              <td className="text-right px-3 py-1.5">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={customDollars[id] ?? 0}
                                  onChange={(e) => handleCustomDollarChange(id, e.target.value)}
                                  className="w-20 bg-surface-800 border border-surface-600 rounded px-2 py-1 text-right text-sm font-mono text-amber-300 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/30"
                                />
                              </td>
                              <td className={`text-right px-3 py-2.5 font-mono ${metricColor(res?.metrics.cagr)}`}>
                                {fmt(res?.metrics.cagr)}
                              </td>
                              <td className="text-right px-3 py-2.5 font-mono text-red-400">
                                {res?.metrics.maxDrawdown !== undefined
                                  ? `-${Math.abs(res.metrics.maxDrawdown).toFixed(1)}%`
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      {/* Total row */}
                      <tr className="border-t border-surface-600/50 bg-surface-800/30">
                        <td className="px-3 py-2.5 font-semibold text-surface-100">Total</td>
                        <td className="text-right px-3 py-2.5 font-mono font-semibold text-primary-300">
                          {(optimization.weights.reduce((s, w) => s + w, 0) * 100).toFixed(1)}%
                        </td>
                        <td className="px-3 py-2.5" />
                        <td className="text-right px-3 py-2.5 font-mono font-semibold text-surface-100">
                          {fmtUSD(portfolioValue)}
                        </td>
                        <td className="text-right px-3 py-2.5 font-mono font-semibold text-amber-300">
                          {fmtUSD(customTotal)}
                          {customTotal !== portfolioValue && (
                            <div className="text-xs text-amber-500 mt-0.5">
                              {customTotal > portfolioValue ? "+" : ""}{fmtUSD(customTotal - portfolioValue)}
                            </div>
                          )}
                        </td>
                        <td className="text-right px-3 py-2.5 font-mono font-semibold" />
                        <td className="text-right px-3 py-2.5 font-mono font-semibold" />
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Custom allocation actions */}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={handleRecalculate}
                    disabled={!hasCustomChanges || isRecalculating}
                    className="btn btn-primary px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isRecalculating ? "Recalculating..." : "Recalculate with Custom $"}
                  </button>
                  {hasCustomChanges && (
                    <button
                      onClick={handleResetCustom}
                      className="btn btn-secondary px-4 py-2 text-sm"
                    >
                      Reset to Optimized
                    </button>
                  )}
                  {customTotal !== portfolioValue && hasCustomChanges && (
                    <span className="text-xs text-surface-500">
                      Weights will be normalised proportionally from your custom amounts
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Combined Equity Curve */}
            {equityChartData && equityChartData.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-base font-semibold text-surface-100 mb-2">
                  Combined Portfolio vs Individual Strategies
                </h3>
                <p className="text-xs text-surface-500 mb-4">
                  Normalised to percentage change from start — shows how the optimised blend compares to each strategy
                </p>
                {/* Time Range Buttons */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {CHART_RANGES.map((r) => (
                    <button
                      key={r.label}
                      onClick={() => setChartTimeRange(r.label)}
                      className={`time-range-btn ${
                        chartTimeRange === r.label ? "time-range-btn-active" : "time-range-btn-inactive"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-0.5 bg-emerald-400 rounded" />
                    <span className="text-xs text-surface-400">Combined Portfolio</span>
                  </div>
                  {adjustedEquityData && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-0.5 bg-amber-400 rounded" />
                      <span className="text-xs text-amber-400 font-medium">Adjusted</span>
                    </div>
                  )}
                  {equityChartData?.[0] && "Benchmark" in equityChartData[0] && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-0.5 bg-surface-400 rounded" style={{ borderTop: "1px dashed #94a3b8" }} />
                      <span className="text-xs text-surface-400">{benchmarkTicker || "SPY"}</span>
                    </div>
                  )}
                  {optimization.labels.map((label, i) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <div
                        className="w-4 h-0.5 rounded"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length], opacity: 0.6 }}
                      />
                      <span className="text-xs text-surface-500">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="h-72 sm:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filteredEquityData ?? []} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d: string) => {
                          const dt = new Date(d + "T12:00:00");
                          return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
                        }}
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={{ stroke: "rgba(148,163,184,0.15)" }}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={60}
                      />
                      <YAxis
                        tickFormatter={(v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`}
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                        width={55}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg text-sm max-w-xs">
                              <p className="text-surface-300 mb-2">{label}</p>
                              {[...payload]
                                .sort((a: { value?: string | number }, b: { value?: string | number }) => Number(b.value) - Number(a.value))
                                .map((p: { dataKey?: string | number; value?: string | number; color?: string }) => (
                                  <div key={p.dataKey} className="flex justify-between gap-4 text-xs">
                                    <span style={{ color: p.color }}>{String(p.dataKey)}</span>
                                    <span className="text-surface-200 font-mono">
                                      {Number(p.value) >= 0 ? "+" : ""}{Number(p.value).toFixed(1)}%
                                    </span>
                                  </div>
                                ))}
                            </div>
                          );
                        }}
                      />
                      {/* Individual strategy lines (thin, translucent) */}
                      {optimization.labels.map((label, i) => (
                        <Line
                          key={label}
                          type="monotone"
                          dataKey={label}
                          stroke={PIE_COLORS[i % PIE_COLORS.length]}
                          strokeWidth={1}
                          strokeOpacity={0.4}
                          dot={false}
                          isAnimationActive={false}
                        />
                      ))}
                      {/* Combined portfolio line (thick, bright) */}
                      <Line
                        type="monotone"
                        dataKey="Combined"
                        stroke="#34d399"
                        strokeWidth={2.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                      {/* Adjusted portfolio line (amber, only when custom allocation recalculated) */}
                      {adjustedEquityData && (
                        <Line
                          type="monotone"
                          dataKey="Adjusted"
                          stroke="#f59e0b"
                          strokeWidth={2.5}
                          dot={false}
                          isAnimationActive={false}
                        />
                      )}
                      {/* Benchmark line (dashed gray) */}
                      <Line
                        type="monotone"
                        dataKey="Benchmark"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Monte Carlo on Combined Portfolio */}
            {monteCarloResult && (
              <div className="glass-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-surface-100">
                        Monte Carlo — Combined Portfolio
                      </h3>
                      <span className="text-xs text-surface-500">
                        5,000 simulations · {monteCarloResult.computeTimeMs.toFixed(0)}ms
                      </span>
                    </div>
                  </div>
                  {/* MC Tabs */}
                  <div className="flex gap-1 bg-surface-800/50 rounded-lg p-0.5">
                    <button
                      onClick={() => setMcTab("fan")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        mcTab === "fan"
                          ? "bg-violet-500/20 text-violet-400"
                          : "text-surface-400 hover:text-surface-200"
                      }`}
                    >
                      Confidence Fan
                    </button>
                    <button
                      onClick={() => setMcTab("distribution")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        mcTab === "distribution"
                          ? "bg-violet-500/20 text-violet-400"
                          : "text-surface-400 hover:text-surface-200"
                      }`}
                    >
                      Distribution
                    </button>
                  </div>
                </div>

                {/* MC Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                  <MCStatCard
                    label="Median CAGR"
                    value={`${monteCarloResult.stats.medianCAGR.toFixed(1)}%`}
                    sub={`Original: ${monteCarloResult.stats.originalCAGR.toFixed(1)}%`}
                    color="text-emerald-400"
                  />
                  <MCStatCard
                    label="CAGR Range (90%)"
                    value={`${monteCarloResult.stats.percentile5CAGR.toFixed(1)}% — ${monteCarloResult.stats.percentile95CAGR.toFixed(1)}%`}
                    color="text-primary-400"
                  />
                  <MCStatCard
                    label="Median Max DD"
                    value={`${monteCarloResult.stats.medianMaxDrawdown.toFixed(1)}%`}
                    sub={`Original: ${monteCarloResult.stats.originalMaxDrawdown.toFixed(1)}%`}
                    color="text-amber-400"
                  />
                  <MCStatCard
                    label="Worst-Case DD (5%)"
                    value={`${monteCarloResult.stats.percentile5MaxDrawdown.toFixed(1)}%`}
                    color="text-red-400"
                  />
                  <MCStatCard
                    label="Profit Probability"
                    value={`${(monteCarloResult.stats.profitProbability * 100).toFixed(1)}%`}
                    color={monteCarloResult.stats.profitProbability >= 0.8 ? "text-emerald-400" : "text-amber-400"}
                  />
                  <MCStatCard
                    label="Ruin Risk (>50%)"
                    value={`${(monteCarloResult.stats.ruinProbability * 100).toFixed(1)}%`}
                    color={monteCarloResult.stats.ruinProbability < 0.05 ? "text-emerald-400" : "text-red-400"}
                  />
                </div>

                {/* Adjusted MC comparison */}
                {adjustedMonteCarloResult && (
                  <div className="mb-5 rounded-lg bg-amber-500/5 border border-amber-500/20 p-4">
                    <h4 className="text-sm font-semibold text-amber-300 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400" />
                      Adjusted Allocation — Monte Carlo
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      <MCStatCard
                        label="Median CAGR"
                        value={`${adjustedMonteCarloResult.stats.medianCAGR.toFixed(1)}%`}
                        sub={`Optimized: ${monteCarloResult.stats.medianCAGR.toFixed(1)}%`}
                        color={adjustedMonteCarloResult.stats.medianCAGR >= monteCarloResult.stats.medianCAGR ? "text-emerald-400" : "text-red-400"}
                      />
                      <MCStatCard
                        label="CAGR Range (90%)"
                        value={`${adjustedMonteCarloResult.stats.percentile5CAGR.toFixed(1)}% — ${adjustedMonteCarloResult.stats.percentile95CAGR.toFixed(1)}%`}
                        color="text-primary-400"
                      />
                      <MCStatCard
                        label="Median Max DD"
                        value={`${adjustedMonteCarloResult.stats.medianMaxDrawdown.toFixed(1)}%`}
                        sub={`Optimized: ${monteCarloResult.stats.medianMaxDrawdown.toFixed(1)}%`}
                        color={adjustedMonteCarloResult.stats.medianMaxDrawdown >= monteCarloResult.stats.medianMaxDrawdown ? "text-emerald-400" : "text-red-400"}
                      />
                      <MCStatCard
                        label="Worst-Case DD (5%)"
                        value={`${adjustedMonteCarloResult.stats.percentile5MaxDrawdown.toFixed(1)}%`}
                        color="text-red-400"
                      />
                      <MCStatCard
                        label="Profit Probability"
                        value={`${(adjustedMonteCarloResult.stats.profitProbability * 100).toFixed(1)}%`}
                        color={adjustedMonteCarloResult.stats.profitProbability >= 0.8 ? "text-emerald-400" : "text-amber-400"}
                      />
                      <MCStatCard
                        label="Ruin Risk (>50%)"
                        value={`${(adjustedMonteCarloResult.stats.ruinProbability * 100).toFixed(1)}%`}
                        color={adjustedMonteCarloResult.stats.ruinProbability < 0.05 ? "text-emerald-400" : "text-red-400"}
                      />
                    </div>
                  </div>
                )}

                {/* MC Confidence Fan Tab */}
                {mcTab === "fan" && (
                  <>
                    <div className="flex flex-wrap items-center gap-4 mb-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-violet-400/40" />
                        <span className="text-surface-400">5th–95th percentile</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-violet-400/70" />
                        <span className="text-surface-400">25th–75th percentile</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-violet-400" />
                        <span className="text-surface-400">Median</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-surface-400">Original</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-1.5 rounded-full bg-surface-400" />
                        <span className="text-surface-400">{benchmarkTicker || "SPY"}</span>
                      </div>
                    </div>
                    <div className="h-72 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={monteCarloResult.bands} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                          <defs>
                            <linearGradient id="mcBandOuter" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.15} />
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.05} />
                            </linearGradient>
                            <linearGradient id="mcBandInner" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(d: string) => {
                              if (d === "start") return "";
                              const dt = new Date(d + "T12:00:00");
                              return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
                            }}
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            axisLine={{ stroke: "rgba(148,163,184,0.15)" }}
                            tickLine={false}
                            interval="preserveStartEnd"
                            minTickGap={60}
                          />
                          <YAxis
                            tickFormatter={(v: number) => fmtUSD(v)}
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            axisLine={false}
                            tickLine={false}
                            width={70}
                            domain={["auto", "auto"]}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0]?.payload;
                              if (!d) return null;
                              return (
                                <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg text-sm">
                                  <p className="text-surface-300 mb-2">{label}</p>
                                  <div className="space-y-1 text-xs">
                                    <div className="flex justify-between gap-4">
                                      <span className="text-emerald-400">Original:</span>
                                      <span className="text-surface-200 font-medium">{fmtUSD(d.original)}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-violet-400">Median:</span>
                                      <span className="text-surface-200 font-medium">{fmtUSD(d.median)}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-violet-300">IQR:</span>
                                      <span className="text-surface-300">{fmtUSD(d.p25)} — {fmtUSD(d.p75)}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-surface-400">90% Range:</span>
                                      <span className="text-surface-300">{fmtUSD(d.p5)} — {fmtUSD(d.p95)}</span>
                                    </div>
                                    {d.benchmark != null && (
                                      <div className="flex justify-between gap-4">
                                        <span className="text-surface-400">{benchmarkTicker || "SPY"}:</span>
                                        <span className="text-surface-200 font-medium">{fmtUSD(d.benchmark)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }}
                          />
                          {/* 5th-95th percentile band (outer) */}
                          <Area type="monotone" dataKey="p95" stroke="none" fill="url(#mcBandOuter)" isAnimationActive={false} />
                          <Area type="monotone" dataKey="p5" stroke="none" fill="#0f172a" isAnimationActive={false} />
                          {/* 25th-75th percentile band (inner) */}
                          <Area type="monotone" dataKey="p75" stroke="none" fill="url(#mcBandInner)" isAnimationActive={false} />
                          <Area type="monotone" dataKey="p25" stroke="none" fill="#0f172a" isAnimationActive={false} />
                          {/* Outer boundary lines */}
                          <Line type="monotone" dataKey="p95" stroke="#8b5cf6" strokeWidth={1} strokeOpacity={0.3} dot={false} isAnimationActive={false} />
                          <Line type="monotone" dataKey="p5" stroke="#8b5cf6" strokeWidth={1} strokeOpacity={0.3} dot={false} isAnimationActive={false} />
                          {/* Inner boundary lines */}
                          <Line type="monotone" dataKey="p75" stroke="#8b5cf6" strokeWidth={1} strokeOpacity={0.5} dot={false} isAnimationActive={false} />
                          <Line type="monotone" dataKey="p25" stroke="#8b5cf6" strokeWidth={1} strokeOpacity={0.5} dot={false} isAnimationActive={false} />
                          {/* Median & Original */}
                          <Line type="monotone" dataKey="median" stroke="#8b5cf6" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                          <Line type="monotone" dataKey="original" stroke="#34d399" strokeWidth={2.5} strokeDasharray="6 3" dot={false} isAnimationActive={false} />
                          {/* Benchmark */}
                          <Line type="monotone" dataKey="benchmark" stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                {/* MC Distribution Tab */}
                {mcTab === "distribution" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* CAGR Distribution */}
                    <div>
                      <h4 className="text-sm font-semibold text-surface-300 mb-3">CAGR Distribution</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={cagrDistribution} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                            <XAxis dataKey="range" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={{ stroke: "rgba(148,163,184,0.15)" }} tickLine={false} interval={4} />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0]?.payload;
                                if (!d) return null;
                                return (
                                  <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-sm">
                                    <span className="text-surface-300">CAGR ~{d.mid.toFixed(1)}%</span>
                                    {d.isOriginal && <span className="ml-2 text-emerald-400 text-xs">◄ Portfolio</span>}
                                  </div>
                                );
                              }}
                            />
                            <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                              {cagrDistribution.map((entry, index) => (
                                <Cell key={index} fill={entry.isOriginal ? "#34d399" : "#8b5cf6"} fillOpacity={entry.isOriginal ? 0.9 : 0.5} />
                              ))}
                            </Bar>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-surface-500">
                        <span>5th: <span className="text-surface-300">{monteCarloResult.stats.percentile5CAGR.toFixed(1)}%</span></span>
                        <span>Median: <span className="text-violet-400 font-medium">{monteCarloResult.stats.medianCAGR.toFixed(1)}%</span></span>
                        <span>95th: <span className="text-surface-300">{monteCarloResult.stats.percentile95CAGR.toFixed(1)}%</span></span>
                      </div>
                    </div>

                    {/* Max Drawdown Distribution */}
                    <div>
                      <h4 className="text-sm font-semibold text-surface-300 mb-3">Max Drawdown Distribution</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={ddDistribution} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                            <XAxis dataKey="range" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={{ stroke: "rgba(148,163,184,0.15)" }} tickLine={false} interval={3} />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0]?.payload;
                                if (!d) return null;
                                return (
                                  <div className="bg-surface-800 border border-surface-600 rounded-lg p-2.5 shadow-lg text-sm">
                                    <span className="text-surface-300">Max DD ~{d.mid.toFixed(1)}%</span>
                                    {d.isOriginal && <span className="ml-2 text-amber-400 text-xs">◄ Portfolio</span>}
                                  </div>
                                );
                              }}
                            />
                            <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                              {ddDistribution.map((entry, index) => (
                                <Cell key={index} fill={entry.isOriginal ? "#fbbf24" : "#ef4444"} fillOpacity={entry.isOriginal ? 0.9 : 0.4} />
                              ))}
                            </Bar>
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-surface-500">
                        <span>Worst (5th): <span className="text-red-400">{monteCarloResult.stats.percentile5MaxDrawdown.toFixed(1)}%</span></span>
                        <span>Median: <span className="text-amber-400 font-medium">{monteCarloResult.stats.medianMaxDrawdown.toFixed(1)}%</span></span>
                        <span>Best (95th): <span className="text-surface-300">{monteCarloResult.stats.percentile95MaxDrawdown.toFixed(1)}%</span></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Disclaimer */}
            <div className="rounded-lg bg-surface-800/50 border border-surface-700/50 p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-surface-400">
                <strong className="text-surface-300">For exploration only.</strong>{" "}
                This is not financial advice. Allocations are based on historical backtested data which does
                not guarantee future results. Correlations and optimal weights change over time.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="rounded-lg bg-surface-800/50 border border-surface-700/50 p-4">
      <div className="text-xs text-surface-500 mb-1">{label}</div>
      <div className={`text-lg font-semibold font-mono ${color}`}>{value}</div>
    </div>
  );
}

function MCStatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
}) {
  return (
    <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/30">
      <div className="text-xs text-surface-500 font-medium uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-surface-500 mt-0.5">{sub}</div>}
    </div>
  );
}
